import {
  Component, EventEmitter, Input, Output,
} from '@angular/core';
import { IUploadedDocument } from '../../models/checkout.models';

@Component({
  selector: 'app-drag-drop-upload',
  templateUrl: './drag-drop-file-upload.component.html',
  styleUrls: ['drag-drop-file-upload.component.scss'],
  standalone: false,
})

export class DragDropFileUploadComponent {
  @Input() supportedMaxAmountOfFiles: number = 1;
  @Input() supportedFileFormates: string[] = []; // Supported file formats, e.g., ['.csv', '.pdf']
  @Input() disabledAddButton: boolean = false; // Disable upload button when true
  @Input() uploadingFileInProgress: boolean = false; // Indicates if a file upload is in progress

  @Input() uploadHeaderText: string = '';  // Header text for the upload section
  @Input() showDownloadCsvTemplateButton: boolean = false; // Input to control download template button visibility
  @Input() showUploadButton: boolean = true; // Input to control upload button visibility, when false, upload emits immediately on file drop
  @Input() maxFileSizeInMB: number = 5; // Individual file size limit in MB
  @Input() maxAllowedFileSizeInMB: number = 20; // Total files size limit in MB
  @Input() fullFramePadding: boolean = true; // When true, the drag-and-drop uses full frame padding

  @Input() files: IUploadedDocument[] | undefined = []; // Files can be passed from parent component as well as managed internally by the component

  /*
  Component emits to parent component array of uploaded files.
   */
  @Output() uploadedFiles: EventEmitter<IUploadedDocument[]> = new EventEmitter<IUploadedDocument[]>();

  maxAmountOfFilesExceededError: boolean = false;
  notSupportedFilesFormatError: boolean = false;
  maxFileSizeExceededError: boolean = false;
  maxAllFilesSizeExceededError: boolean = false;
  duplicateFilesError: string = '';


  /**
   * Method to handle files dropped via drag-and-drop or selected via file dialog
   *
   * @param files - files dropped or selected
   */
  handleDragDropFiles(files: File[]): void {
    const newFiles: IUploadedDocument[] = this.mapFilesToUploadedDocuments(files);
    this.clearErrors();

    if (!this.files || this.files.length === 0) {
      this.handleNewFiles(newFiles);
    } else {
      this.handleAddingToExistingFiles(newFiles);
    }

    // Automatically upload files if upload button is not shown
    if (this.files && this.showUploadButton === false) {
      this.uploadFiles();
    }
  }

  /**
   * Maps File objects to IUploadedDocument objects with initial state
   *
   * @param files - array of File objects to map
   * @returns array of IUploadedDocument objects
   */
  private mapFilesToUploadedDocuments(files: File[]): IUploadedDocument[] {
    return Array.from(files).map(file => ({
      file,
      uploadingInProgress: false,
      uploadSuccess: false,
      uploadError: null,
    }));
  }

  /**
   * Clears all error messages related to file upload validation
   */
  private clearErrors(): void {
    this.maxAmountOfFilesExceededError = false;
    this.notSupportedFilesFormatError = false;
    this.maxFileSizeExceededError = false;
    this.maxAllFilesSizeExceededError = false;
    this.duplicateFilesError = '';
  }

  /**
   * Handles new files dropped or selected, validating them against constraints
   *
   * @param newFiles - array of new files to be handled
   */
  private handleNewFiles(newFiles: IUploadedDocument[]): void {
    if (newFiles.length > this.supportedMaxAmountOfFiles) {
      this.maxAmountOfFilesExceededError = true;
      this.files = [];
      return;
    }

    if (!this.validateFileFormats(newFiles)) {
      this.notSupportedFilesFormatError = true;
      this.files = [];
      return;
    }

    if (!this.validateIndividualFileSizes(newFiles)) {
      this.maxFileSizeExceededError = true;
      this.files = [];
      return;
    }

    if (!this.validateTotalFileSize(newFiles, [])) {
      this.maxAllFilesSizeExceededError = true;
      this.files = [];
      return;
    }

    this.files = newFiles;
  }

  /**
   * Handles new files dropped or selected, validating them against constraints
   *
   * @param newFiles - array of new files to be added to existing files
   */
  private handleAddingToExistingFiles(newFiles: IUploadedDocument[]): void {
    // Filter out duplicate files by name and set error message
    const {nonDuplicateFiles, duplicateFileNames} = this.filterDuplicateFiles(newFiles);

    if (duplicateFileNames.length > 0) {
      this.duplicateFilesError = duplicateFileNames.join(', ');
    }

    if (nonDuplicateFiles.length === 0) {
      return;
    }

    const totalCount: number = this.files!.length + nonDuplicateFiles.length;

    if (totalCount > this.supportedMaxAmountOfFiles) {
      this.maxAmountOfFilesExceededError = true;
      return;
    }

    const validFiles: IUploadedDocument[] = this.filterValidFiles(nonDuplicateFiles);

    if (validFiles.length === 0 || this.notSupportedFilesFormatError) {
      return;
    }

    if (!this.validateIndividualFileSizes(validFiles)) {
      this.maxFileSizeExceededError = true;
      return;
    }

    if (!this.validateTotalFileSize(validFiles, this.files!)) {
      this.maxAllFilesSizeExceededError = true;
      return;
    }

    this.files = [...this.files!, ...validFiles];
  }

  /**
   * Validates if all files have supported formats
   *
   * @param files - array of files to validate formats
   * @returns boolean indicating if all files have supported formats
   */
  private validateFileFormats(files: IUploadedDocument[]): boolean {
    return files.every(doc => this.hasFileSupportedFormat(doc.file.name));
  }

  /**
   * Filters files to include only those with supported formats
   *
   * @param files - array of files to filter valid formats
   * @returns array of files with supported formats
   */
  private filterValidFiles(files: IUploadedDocument[]): IUploadedDocument[] {
    const validFiles: IUploadedDocument[] = [];

    for (const doc of files) {
      if (this.hasFileSupportedFormat(doc.file.name)) {
        validFiles.push(doc);
      } else {
        this.notSupportedFilesFormatError = true;
      }
    }

    return validFiles;
  }

  /**
   * Filters out files that already exist in the current files array by comparing file names
   *
   * @param newFiles - array of new files to check for duplicates
   * @returns object containing array of files without duplicates and array of duplicate file names
   */
  private filterDuplicateFiles(newFiles: IUploadedDocument[]): {
    nonDuplicateFiles: IUploadedDocument[],
    duplicateFileNames: string[]
  } {
    if (!this.files || this.files.length === 0) {
      return {nonDuplicateFiles: newFiles, duplicateFileNames: []};
    }

    const existingFileNames: Set<string> = new Set(this.files.map(doc => doc.file.name));
    const nonDuplicateFiles: IUploadedDocument[] = [];
    const duplicateFileNames: string[] = [];

    for (const doc of newFiles) {
      if (existingFileNames.has(doc.file.name)) {
        duplicateFileNames.push(doc.file.name);
      } else {
        nonDuplicateFiles.push(doc);
      }
    }

    return {nonDuplicateFiles, duplicateFileNames};
  }

  /**
   * Validates if all files are under the maximum allowed size
   *
   * @param files - array of files to validate sizes
   * @returns boolean indicating if all files are under max allowed size
   */
  private validateIndividualFileSizes(files: IUploadedDocument[]): boolean {
    const maxSizeInBytes: number = this.maxFileSizeInMB * 1024 * 1024;
    return files.every(doc => doc.file.size <= maxSizeInBytes);
  }

  /**
   * Validates if total size of new and existing files is under the maximum allowed size
   *
   * @param newFiles - array of new files to validate total size
   * @param existingFiles - array of existing files to validate total size
   * @returns boolean indicating if total size of new and existing files is under max allowed size
   */
  private validateTotalFileSize(newFiles: IUploadedDocument[], existingFiles: IUploadedDocument[]): boolean {
    const maxTotalSizeInBytes: number = this.maxAllowedFileSizeInMB * 1024 * 1024;
    const existingSize: number = existingFiles.reduce((sum, doc) => sum + doc.file.size, 0);
    const newFilesSize: number = newFiles.reduce((sum, doc) => sum + doc.file.size, 0);
    const totalSize: number = existingSize + newFilesSize;
    return totalSize <= maxTotalSizeInBytes;
  }

  /**
   * Validates if the file has a supported format
   *
   * @param fileName - name of checked file
   * @returns boolean indicating if the file has a supported format
   */
  hasFileSupportedFormat(fileName: string): boolean {
    return this.supportedFileFormates?.some(format => fileName.toLowerCase().endsWith(format));
  }

  /**
   * Uploads the files by emitting them to the parent component
   */
  uploadFiles(): void {
    this.uploadedFiles.emit(this.files);

    if (this.showUploadButton) {
      this.files = [];
    }
  }

  /**
   * Deletes uploaded files, either a specific file or all files
   *
   * @param fileToDelete - specific file to delete
   */
  deleteUploadedFiles(fileToDelete?: IUploadedDocument): void {
    if (fileToDelete && this.files) {
      // Remove only the specific file
      this.files = this.files.filter(doc => doc !== fileToDelete);

      // If no files left, set to []
      if (this.files.length === 0) {
        this.files = [];
      }
    } else {
      // If no specific file provided, clear all files
      this.files = [];
    }

    if (this.showUploadButton === false) {
      this.uploadFiles();
    }

    this.clearErrors();
  }

  /**
   * Functionality supports downloading only one file at a time
   */

  createCsv(filename: string): void {
    const csvContent: string = this.convertToCsv();
    this.downloadCsv(csvContent, filename);
  }

  private convertToCsv(): string {
    const headers: string[] = ['Material_Number', 'Quantity'];
    return headers.join(',') + '\n';
  }

  private downloadCsv(content: string, filename: string): void {
    const blob: Blob = new Blob([content], {type: 'text/csv'});
    const url: string = window.URL.createObjectURL(blob);

    const a: HTMLAnchorElement = document.createElement('a');
    a.href = url;
    a.download = filename + '.csv';
    a.click();

    window.URL.revokeObjectURL(url);
  }
}
